import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { ScrollTrigger } from '@/lib/gsap'
import { prefersReducedMotion } from '@/lib/motion'
import { usePageTransition } from '@/components/motion/transition-context'

interface ScrambleTextProps {
    text: string
    className?: string
    duration?: number
    /** "view" scrambles once when scrolled into view, "enter" when the page opens. */
    trigger?: 'view' | 'enter'
    replayOnHover?: boolean
}

const GLYPHS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789#%&*+'

/** Letters shuffle through random glyphs before settling on the real text. */
const ScrambleText: FC<ScrambleTextProps> = ({
    text,
    className,
    duration = 1.3,
    trigger = 'view',
    replayOnHover = true
}): ReactNode => {
    const { ready, enterDelay } = usePageTransition()
    const output = useRef<HTMLSpanElement>(null)
    const frame = useRef<number | null>(null)

    const run = (): void => {
        const element = output.current

        if (!element || prefersReducedMotion()) return
        if (frame.current !== null) window.cancelAnimationFrame(frame.current)

        const chars = Array.from(text)
        const settle = chars.map((_, index) => (index / chars.length) * duration * 0.75 + Math.random() * duration * 0.25)
        const startedAt = performance.now()

        const step = (now: number): void => {
            const elapsed = (now - startedAt) / 1000
            let done = true

            element.textContent = chars.map((char, index) => {
                if (char === ' ' || elapsed >= settle[index]) return char

                done = false

                return GLYPHS[Math.floor(Math.random() * GLYPHS.length)]
            }).join('')

            if (done) {
                element.textContent = text
                frame.current = null

                return
            }

            frame.current = window.requestAnimationFrame(step)
        }

        frame.current = window.requestAnimationFrame(step)
    }

    useEffect(() => {
        const element = output.current

        if (!ready || !element) return

        if (trigger === 'enter') {
            const timeout = window.setTimeout(run, enterDelay * 1000 + 200)

            return () => window.clearTimeout(timeout)
        }

        const scrollTrigger = ScrollTrigger.create({
            trigger: element,
            start: 'top 88%',
            once: true,
            onEnter: run
        })

        return () => scrollTrigger.kill()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ready, trigger, text])

    useEffect(() => () => {
        if (frame.current !== null) window.cancelAnimationFrame(frame.current)
    }, [])

    return (
        <span className={className} onPointerEnter={replayOnHover ? run : undefined}>
            <span className='sr-only'>
                {text}
            </span>

            <span ref={output} aria-hidden='true'>
                {text}
            </span>
        </span>
    )
}

export default ScrambleText
